<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const props = withDefaults(
  defineProps<{
    variant?: "dark" | "light";
    menuPlacement?: "top" | "bottom";
  }>(),
  {
    variant: "dark",
    menuPlacement: "top",
  },
);

const { locale, locales, setLocale } = useI18n();
type LocaleCode = typeof locale.value;

const availableLocales = computed(() =>
  (locales.value as Array<{ code: LocaleCode; name?: string }>).map((l) => ({
    code: l.code,
    name: l.name ?? l.code.toUpperCase(),
  })),
);

const currentLocaleName = computed(
  () =>
    availableLocales.value.find((l) => l.code === locale.value)?.name ??
    locale.value.toUpperCase(),
);

const open = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const buttonClass = computed(() =>
  props.variant === "light"
    ? "flex items-center gap-1.5 rounded-full border border-white/70 bg-white/85 px-3 py-1.5 text-xs font-semibold text-[#2e234b] shadow-[0_10px_24px_rgba(46,35,75,0.08)] transition-colors hover:bg-white"
    : "flex items-center gap-1.5 text-xs text-gray-400 transition-colors hover:text-white",
);

const menuClass = computed(() => {
  const placement =
    props.menuPlacement === "bottom" ? "top-full mt-2" : "bottom-full mb-1";
  const surface =
    props.variant === "light"
      ? "border border-[#eadffb] bg-white/95 shadow-[0_22px_44px_-26px_rgba(46,35,75,0.28)]"
      : "border border-white/10 bg-[#3d2f5c] shadow-lg";

  return `absolute right-0 z-20 min-w-[120px] overflow-hidden rounded-lg ${placement} ${surface}`;
});

function optionClass(code: LocaleCode) {
  const isCurrent = code === locale.value;

  if (props.variant === "light") {
    return isCurrent
      ? "bg-[#f5eefc] text-[#2e234b]"
      : "text-gray-600 hover:bg-[#faf6ff] hover:text-[#2e234b]";
  }

  return isCurrent
    ? "bg-white/10 text-white"
    : "text-gray-300 hover:bg-white/5 hover:text-white";
}

function selectLocale(code: LocaleCode) {
  setLocale(code);
  open.value = false;
}

function onClickOutside(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    open.value = false;
  }
}

onMounted(() => document.addEventListener("click", onClickOutside));
onBeforeUnmount(() => document.removeEventListener("click", onClickOutside));
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      type="button"
      :class="buttonClass"
      aria-label="Select language"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="open = !open"
    >
      <svg
        class="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"
        />
      </svg>
      {{ currentLocaleName }}
      <svg
        class="h-3 w-3 transition-transform"
        :class="{ 'rotate-180': open }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
    <div v-if="open" :class="menuClass">
      <button
        v-for="loc in availableLocales"
        :key="loc.code"
        type="button"
        class="w-full px-3 py-2 text-left text-xs transition-colors"
        :class="optionClass(loc.code)"
        @click="selectLocale(loc.code)"
      >
        {{ loc.name }}
      </button>
    </div>
  </div>
</template>
